/**
 * Client-side helpers for GET /bank/resolve-swift autofill.
 * The backend owns the SWIFT directory; these only decide when to call and
 * whether applying the result would overwrite a manual edit.
 */

export interface SwiftBankResolve {
  swift: string;
  name: string;
  countryCode: string;
  city: string | null;
  branch: string | null;
}

/** Uppercase SWIFT with spaces/dashes removed. */
export function normalizeSwiftCode(raw: string | null | undefined): string {
  if (raw == null) return '';
  return String(raw).replace(/[\s-]/g, '').toUpperCase();
}

/** Standard SWIFT/BIC lengths: 8 (bank) or 11 (with branch). */
export function looksLikeSwiftCode(swift: string | null | undefined): boolean {
  const normalized = normalizeSwiftCode(swift);
  if (normalized.length !== 8 && normalized.length !== 11) return false;
  return /^[A-Z0-9]+$/.test(normalized);
}

/**
 * Whether to call resolve-swift for this form state.
 * Skip when SWIFT input is hidden (ARM withdrawal view).
 */
export function shouldResolveSwiftBank(opts: {
  swift?: string | null;
  viewType?: string | null;
}): boolean {
  const swift = opts.swift?.trim() ?? '';
  if (!swift) return false;
  if (opts.viewType === 'arm') return false;
  return looksLikeSwiftCode(swift);
}

export interface SwiftBankAutofillInput {
  bank: SwiftBankResolve | null | undefined;
  currentName: string;
  currentCountry: string;
  /**
   * Form-ready country value derived by the caller from `bank.countryCode`
   * (e.g. ISO code for admin, country name for client withdrawal).
   */
  resolvedCountry: string;
  lastAutofilledName?: string | null;
  lastAutofilledCountry?: string | null;
}

export interface SwiftBankAutofillResult {
  bankName?: string;
  country?: string;
  lastAutofilledName?: string;
  lastAutofilledCountry?: string;
}

/**
 * Returns only fields that should be written. Empty result means leave the form alone.
 * Caller must supply `resolvedCountry` mapped from the API country code.
 *
 * Bank name and country are always overwritten when a valid bank is resolved,
 * since the SWIFT directory is authoritative.
 */
export function applySwiftBankAutofill(
  input: SwiftBankAutofillInput
): SwiftBankAutofillResult {
  const bank = input.bank;
  if (!bank?.name) return {};

  const out: SwiftBankAutofillResult = {};

  out.bankName = bank.name;
  out.lastAutofilledName = bank.name;

  const resolved = (input.resolvedCountry || '').trim();
  if (resolved) {
    out.country = resolved;
    out.lastAutofilledCountry = resolved;
  }

  return out;
}
